import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import { isDate } from 'validator';

// Our Components
import Alert from 'components/Alert';
import { PrimaryButton } from 'components/Button/Button';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Input from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import StandardDatePicker from 'components/DatePicker/StandardDatePicker';
import SoraTextField from 'components/Input/SoraTextField';
import { NUMBER, TEXT } from 'components/Input/Types';
import RequiredFieldsTooltip from 'components/Forms/NewLoanForms/RequiredFieldsTooltip';

// Our Query Keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Utils
import {
	clearFormValues,
	getDateFromDateObject,
	isSubmissionReady
} from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our Emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import {
	EMPLOYMENT_STATUS_OPTIONS,
	NEW_LOAN_OFFER_TYPE,
	PERSONAL
} from 'shared/constants';

function NewPersonalLoanForm({
	advisorInfo,
	clientInfo,
	clientProfileData,
	isClientNameProvided,
	financialData,
	loanRequestId,
	loanType
}) {
	const queryClient = useQueryClient();
	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const updateLoanInquiry = useMutateUpdateLoanInquiry();
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Advisor Info
	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Client Info
	const {
		annualIncome,
		assetsAmount,
		clientFullName,
		creditScoreValue,
		clientId,
		emailAddressValue,
		firstName,
		lastName
	} = clientInfo;

	// Pre-populated state items
	const [employmentStatus, setEmploymentStatus] = useState('Employed');
	const [employer, setEmployer] = useState('');
	const [jobTitle, setJobTitle] = useState('');
	const [rentOrOwn, setRentOrOwn] = useState('');
	const [monthlyHousingPayment, setMonthlyHousingPayment] = useState('');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const [startDate, setStartDate] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [ownershipTenure, setOwnershipTenure] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const isValidDate = useMemo(() => {
		const isStringDate = typeof startDate === 'string';
		// @ts-ignore
		const isDateType = startDate instanceof Date;
		if (isStringDate && startDate !== '') {
			const isDateCheckWithConversion = isDate(new Date(startDate));
			return isDateCheckWithConversion;
		}

		if (isDateType) {
			return isDate(startDate);
		}
		// if dob is null this returns false;
		return false;
	}, [startDate]);

	const formData = [loanAmount];

	const loanRequestDetailsRequiredFields = [
		loanAmount,
		rentOrOwn,
		monthlyHousingPayment,
		employmentStatus,
		startDate,
		addressLine1,
		city,
		state,
		zipCode
	];

	const isFormReady = useMemo(
		() =>
			isSubmissionReady(
				atLoanRequestDetailsUrl
					? loanRequestDetailsRequiredFields
					: formData
			) && isClientNameProvided,
		atLoanRequestDetailsUrl
			? loanRequestDetailsRequiredFields
			: [...formData, isClientNameProvided]
	);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `New Personal Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Client's rent or own status: ${rentOrOwn}`,
				`Client's desired loan term: ${ownershipTenure}`,
				`Client's monthly housing cost: ${monthlyHousingPayment}`,
				`Client's Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`,
				`Client's Employment status: ${employmentStatus}`,
				`Client's Employment Start data: ${getDateFromDateObject(
					// @ts-ignore
					startDate
				)}`,
				`Client's Employer: ${employer}`,
				`Client's Job title: ${jobTitle}`
			],
			additionalNotes,
			lendingTypeRequested: 'New Personal Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setEmployer,
						setEmploymentStatus,
						setJobTitle,
						setLoanAmount,
						setOwnershipTenure,
						setStartDate,
						setState,
						setZipCode
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScore: creditScoreValue,
			dob: startDate,
			emailAddress: emailAddressValue,
			employer,
			employmentStatus,
			firstName,
			jobTitle,
			lastName,
			loanAmount,
			monthlyHousingPayment,
			offerType: NEW_LOAN_OFFER_TYPE,
			ownershipTenure,
			rentOrOwn,
			startDate,
			state,
			totalAnnualIncome: annualIncome,
			totalAssetsValue: assetsAmount,
			tradelineType: PERSONAL,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			creditScore: creditScoreValue,
			dob: startDate,
			emailAddress: emailAddressValue,
			employer,
			employmentStatus,
			firstName,
			jobTitle,
			lastName,
			loanAmount,
			monthlyHousingPayment,
			offerType: NEW_LOAN_OFFER_TYPE,
			ownershipTenure,
			rentOrOwn,
			startDate,
			state,
			totalAnnualIncome: annualIncome,
			totalAssetsValue: assetsAmount,
			tradelineType: PERSONAL,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setEmployer,
						setEmploymentStatus,
						setJobTitle,
						setLoanAmount,
						setOwnershipTenure,
						setStartDate,
						setState,
						setZipCode
					]);
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: annualIncome,
				label: 'Annual Income:'
			},
			{
				type: 'currency',
				value: assetsAmount,
				label: 'Total assets value:'
			},
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired loan amount:'
			},
			{
				type: 'currency',
				value: monthlyHousingPayment,
				label: 'Monthly rent or mortgage payment:'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScoreValue}
				Desired loan term: ${ownershipTenure}
				Primary residence status: ${rentOrOwn}
				Employment status: ${employmentStatus}
				Employer name: ${employer}
				Employment start date: ${startDate}
				Job title: ${jobTitle}
				Address: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				${formattedCollection}
				State: ${state}
				------------------------------------------------------------------------------------------------
				Advisor's notes: ${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						New Personal Loan
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />

					<Grid item xs={12} md={6}>
						{/* Desired Loan Amount */}
						<Input
							label="Desired loan amount *"
							value={loanAmount}
							onChange={setLoanAmount}
							type={NUMBER}
							inputProps={{
								'data-test': 'loanAmount'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Loan Tenure */}
						<Input
							type={NUMBER}
							label="Desired loan term *"
							subLabel="Length of loan in years"
							helperText="Down payment cannot be empty"
							onChange={setOwnershipTenure}
							value={ownershipTenure}
							inputProps={{
								'data-test': 'Desired loan term'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Rent or own */}
						<Dropdown
							items={[
								'Renter',
								'Homeowner',
								'Live with a family member',
								'Other'
							]}
							selected={rentOrOwn}
							onChange={setRentOrOwn}
							variant="outlined"
							label={
								atLoanRequestDetailsUrl
									? 'Primary residence status *'
									: 'Primary residence status'
							}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Monthly Rent or Mortgage Payment Amount */}
						<CurrencyTextInput
							label={
								atLoanRequestDetailsUrl
									? 'Monthly rent or mortgage payment *'
									: 'Monthly rent or mortgage payment'
							}
							value={monthlyHousingPayment}
							onChange={setMonthlyHousingPayment}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Employment Status */}
						<Dropdown
							items={EMPLOYMENT_STATUS_OPTIONS}
							selected={employmentStatus}
							onChange={setEmploymentStatus}
							variant="outlined"
							label={
								atLoanRequestDetailsUrl
									? 'Employment status *'
									: 'Employment status'
							}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Employer */}
						<Input
							label="Employer name"
							value={employer}
							onChange={setEmployer}
							type={TEXT}
							inputProps={{
								'data-test': 'employer'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Start date */}
						<StandardDatePicker
							id="startDate"
							label={
								atLoanRequestDetailsUrl
									? 'Employment start date *'
									: 'Employment start date'
							}
							onChange={setStartDate}
							value={startDate}
							error={startDate === null || !isValidDate}
							errorText="Start date needs to be a valid date"
							inputProps={{
								'data-test': 'startDate'
							}}
						/>
					</Grid>

					<Grid item xs={12} md={6}>
						{/* Job Title */}
						<Input
							label="Job title"
							value={jobTitle}
							onChange={setJobTitle}
							type={TEXT}
							inputProps={{
								'data-test': 'jobTitle'
							}}
						/>
					</Grid>

					<GetFullAddressPartialForm
						label={
							atLoanRequestDetailsUrl ? 'Address *' : 'Address'
						}
						addressLine1={addressLine1}
						addressLine2={addressLine2}
						city={city}
						state={state}
						zipCode={zipCode}
						setAddressLine1={setAddressLine1}
						setAddressLine2={setAddressLine2}
						setCity={setCity}
						setState={setState}
						setZipCode={setZipCode}
					/>

					<Grid item xs={12}>
						<SoraTextField
							label="Additional Notes"
							value={additionalNotes}
							onChange={setAdditionalNotes}
						/>
					</Grid>
				</>
			)}

			<Grid item xs={12}>
				{!isFormReady && (
					<RequiredFieldsTooltip>
						<span>
							<PrimaryButton
								disabled={!isFormReady}
								onClick={submitForm}
							>
								Submit
							</PrimaryButton>
						</span>
					</RequiredFieldsTooltip>
				)}
				{isFormReady && (
					<PrimaryButton onClick={submitForm}>Submit</PrimaryButton>
				)}
			</Grid>
		</>
	);
}

export default NewPersonalLoanForm;
